import React, { useEffect, useState } from "react";
import Pusher from "pusher-js";
import { useRouter } from "next/router";
import {
  getLatestBid_AuctionFloor,
  getAllBidsForBanker_AuctionFloor,
  getAllWrongBidsForBanker_AuctionFloor,
  getBidderSessionData_AuctionFloor,
  getAllAnnouncementsForBanker,
  getAllBiddersForEvent,
} from "services";
import { DateTime } from "luxon";
import BankerEventDataV3 from "./BankerEventDataV3";
import AuctionLoader from "../../../bidder/Tabs/auctionFloor/AuctionLoader";
import FloatingOnlineUsers from "../../FloatingOnlineUsers";
import { useSelector } from "react-redux";

interface BankerAuctionFloorV3Props {
  data: {
    uuid: string;
    status: string;
    start_date_time: string;
    reserve_price: string;
    final_end_date: string;
    server_time?: number | string;
  };
}

function BankerAuctionFloorV3(props: BankerAuctionFloorV3Props) {
  const [amount, setAmount] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [status, setStatus] = useState("");
  const [serverTime, setServerTime] = useState(0);
  const [currentTime, setCurrentTime] = useState<any>(0);
  const [bidsEvent, setBidsEvent] = useState<any[]>([]);
  const [latestBid, setLatestBid] = useState<any>(null);
  const [prevBid, setPrevBid] = useState<any>(null);
  const [wrongBidsEvent, setWrongBidsEvent] = useState<any[]>([]);
  const [loading, setLoading] = useState<any>(true);
  const [onlineMembers, setOnlineMembers] = useState<any[]>([]);
  const [bidderSessionData, setBidderSessionData] = useState<any>(null);
  const [isOnlineEventTriggered, setIsOnlineEventTriggered] =
    useState<any>(false);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [bidders, setBidders] = useState<any[]>([]);

  const {
    query: { id },
  } = useRouter();

  const router = useRouter();
  const accessToken = useSelector((state: any) => state.bankerAuth.accessToken);
  const bankerName = useSelector(
    (state: any) => state.bankerAuth.user?.profile?.bio?.full_name,
  );

  // pusher functionality
  useEffect(() => {
    Pusher.logToConsole = false;
    let channelId = `presence-auction-event-${id}`;
    let authConfig = {
      authEndpoint: `${process.env.NEXT_PUBLIC_API_URL}/auction-floor/pusher/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          event_uuid: id,
          user_type: "banker",
          user_name: bankerName,
        },
      },
    };

    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
      ...authConfig,
    });

    const channel = pusher.subscribe(channelId);

    channel.bind("bidding_value_has_been_changed", (data: any) => {
      handleDataChange(data);
      setBidsEvent((prev) => [...prev, data]);
    });

    channel.bind("user_placed_wrong_bid", (data: any) => {
      setWrongBidsEvent((prev) => [...prev, data]);
    });

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        if (data.server_time) {
          setServerTime(Number(data.server_time));
        }
        setStatus("live");
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        setStatus("end");
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
      // setConnectionStatus("Offline");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
      // setConnectionStatus("Online");
    });

    channel.bind("auction_event_has_announcement", (data: any) => {
      console.log("auction_event_has_announcement----", data);

      if (data) {
        setAnnouncements((prev) => [...prev, data]);
      }
    });

    //----------Pusher members---------
    // Set up presence events
    channel.bind("pusher:subscription_succeeded", (members: any) => {
      const memberList = Object.entries(members.members).map(
        ([id, member]: [string, any]) => ({
          id: id,
          name: member.name,
          user_type: member.user_type,
        }),
      );
      setOnlineMembers(memberList);
    });

    channel.bind("pusher:member_added", (member: any) => {
      setOnlineMembers((prev: any) => {
        // Check if member already exists to prevent duplicates
        const exists = prev.some((m: any) => m.id === member.id);
        if (exists) {
          return prev;
        }
        return [
          ...prev,
          {
            id: member.id,
            name: member.info.name,
            user_type: member.info.user_type,
          },
        ];
      });
    });

    channel.bind("pusher:member_removed", (member: any) => {
      setOnlineMembers((prev: any) =>
        prev.filter((m: any) => m.id !== member.id),
      );
    });
    return function cleanup() {
      pusher.disconnect();
    };
  }, [id]);

  useEffect(() => {
    if (props.data) {
      (async () => {
        const allBid: any = await getAllBidsForBanker_AuctionFloor(id);

        const allWrongBids: any = await getAllWrongBidsForBanker_AuctionFloor(
          id,
        );

        if (allBid) {
          setBidsEvent(allBid);
          if (allBid.length >= 2) {
            setPrevBid(allBid[allBid.length - 2]);
          }
        }
        if (allWrongBids) {
          setWrongBidsEvent(allWrongBids);
        }
        const lastBid: any = await getLatestBid_AuctionFloor(id);
        setLatestBid(lastBid);
        setStatus(props.data?.status);
        if (props.data?.server_time) {
          setServerTime(Number(props.data.server_time));
        }
        const start_timestamp = DateTime.fromISO(props.data?.start_date_time)
          .setZone("Asia/Kolkata")
          .toMillis();
        setStartTime(start_timestamp);

        if (lastBid) {
          setAmount(lastBid.final_amount);
          setEndTime(parseInt(lastBid.final_end_time));
        } else {
          setAmount(parseInt(props.data?.reserve_price));
          setEndTime(parseInt(props.data?.final_end_date));
        }
        const allAnnouncements: any = await getAllAnnouncementsForBanker(id);
        console.log("allAnnouncements", allAnnouncements);
        if (allAnnouncements.length > 0) {
          setAnnouncements(allAnnouncements);
        }
        const allBidders: any = await getAllBiddersForEvent(id as string);

        if (allBidders.length > 0) {
          setBidders(allBidders);
        }
        setLoading(false);
      })();
    }
  }, [props.data]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(
        DateTime.now().setZone("Asia/Kolkata").toFormat("hh:mm:ss a"),
      );
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isOnlineEventTriggered) {
      router.reload();
    }
  }, [isOnlineEventTriggered]);

  const handleDataChange = async (data: any) => {
    if (data?.server_time) {
      setServerTime(Number(data.server_time));
    }
    setAmount(data?.final_amount);
    setEndTime(data?.final_end_time);
    if (latestBid) {
      setPrevBid(latestBid);
    }

    const lastBid: any = await getLatestBid_AuctionFloor(id);

    setLatestBid((prev: any) => {
      setPrevBid(prev);
      return lastBid;
    });
  };

  const getBidderSessionDataForBanker = async (id: any) => {
    try {
      const data = await getBidderSessionData_AuctionFloor(id);
      setBidderSessionData(data);
      return data;
    } catch (error) {
      console.error("Error fetching session data:", error);
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        await getBidderSessionDataForBanker(id);
      }
    })();
  }, [id, status]);

  return (
    <div className="rounded-lg">
      <div className="mb-8">
        {loading ? (
          <AuctionLoader />
        ) : (
          <>
            <BankerEventDataV3
              id={id}
              data={props.data}
              serverTime={serverTime}
              latestBidAmount={amount}
              startTime={startTime}
              endTime={endTime}
              status={status}
              setStatus={setStatus}
              currentTime={currentTime}
              latestBid={latestBid}
              prevBid={prevBid}
              correctBids={bidsEvent}
              wrongBids={wrongBidsEvent}
              announcements={announcements}
              bidders={bidders}
            />
            <FloatingOnlineUsers
              onlineMembers={onlineMembers}
              status={status}
              bidderSessionData={bidderSessionData}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default BankerAuctionFloorV3;
